/**
 * Which configured way to sign in this browser uses (workforce SSO, 2026-09-28).
 *
 * Each provider is its own MSAL client, and only the client that started a redirect can finish
 * it on `/callback`, renew its token silently and sign it out. So the choice is written just
 * before the redirect leaves, and read when MSAL starts:
 *
 * - **sessionStorage** is this tab's choice. It wins, so a second tab that picks another
 *   provider cannot move this tab's session (or its in-flight `/callback`) to the wrong client.
 * - **localStorage** is the provider remembered for the next visit, in a new tab. Since #1644 it
 *   holds only a provider that produced a session: it is written once `/api/v2/me` accepts the
 *   person through this tab's choice (`rememberSignedInProvider`), and it is removed the moment
 *   a redirect leaves (`writeProviderChoice`), so a round trip that never comes back, is
 *   cancelled, finds no account at the identity provider or is refused by skyData leaves nothing
 *   remembered. An unauthenticated visit goes straight to the remembered provider only
 *   (`useAutoSignIn`), so the app never sends a person to a provider twice without a session in
 *   between, and the sign-in page with its choice of providers is always the way back.
 *
 * The remembered value has its own key, `REMEMBERED_PROVIDER_KEY`: the old one
 * (`PROVIDER_CHOICE_KEY` in localStorage) was written before the redirect left, so it cannot say
 * whether the provider ever worked. It is never read again and is removed on every write.
 *
 * Only an id is stored, never a token or an address. Blocked storage is no choice: MSAL starts
 * on the default provider, which is what a deployment with one provider always does.
 */

export const PROVIDER_CHOICE_KEY = "skydata-auth-provider";

/** localStorage: the provider that last produced a session in this browser (#1644). */
export const REMEMBERED_PROVIDER_KEY = "skydata-auth-provider-signed-in";

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, id: string | null): void {
  try {
    if (id === null) storage().removeItem(key);
    else storage().setItem(key, id);
  } catch {
    /* site data blocked; the next start uses the default provider */
  }
}

function writeRemembered(id: string | null): void {
  write(() => localStorage, REMEMBERED_PROVIDER_KEY, id);
  // The pre-#1644 value, written before a redirect: never trusted, so never kept.
  write(() => localStorage, PROVIDER_CHOICE_KEY, null);
}

/** This tab's choice only, or null. */
export function readTabProviderChoice(): string | null {
  return read(() => sessionStorage, PROVIDER_CHOICE_KEY);
}

/** The provider that produced a session in this browser before, or null (#1644). */
export function readRememberedProvider(): string | null {
  return read(() => localStorage, REMEMBERED_PROVIDER_KEY);
}

/** The chosen provider id: this tab's, else the remembered one, else null. */
export function readProviderChoice(): string | null {
  return readTabProviderChoice() ?? readRememberedProvider();
}

/** Both stored values, so a failed redirect can put them back exactly. */
export interface ProviderChoiceSnapshot {
  tab: string | null;
  remembered: string | null;
}

export function snapshotProviderChoice(): ProviderChoiceSnapshot {
  return { tab: readTabProviderChoice(), remembered: readRememberedProvider() };
}

/**
 * Record `id` for this tab, just before its redirect leaves. The remembered provider is forgotten
 * until this round trip produces a session (#1644): a visit in a new tab meanwhile shows the
 * sign-in page instead of sending the person back to a provider that has not let them in.
 */
export function writeProviderChoice(id: string): void {
  write(() => sessionStorage, PROVIDER_CHOICE_KEY, id);
  writeRemembered(null);
}

/** `/api/v2/me` accepted the person: remember this tab's provider for the next visit (#1644). */
export function rememberSignedInProvider(): void {
  const tab = readTabProviderChoice();
  if (tab !== null && readRememberedProvider() !== tab) writeRemembered(tab);
}

/** skyData refused the person: nothing is remembered, so the next visit offers the choice. */
export function forgetRememberedProvider(): void {
  if (readRememberedProvider() !== null) writeRemembered(null);
}

/**
 * Forget every provider choice, this tab's too (#1644): `/sign-in?choose`, the guaranteed way
 * back to the choice of providers.
 */
export function forgetProviderChoice(): void {
  write(() => sessionStorage, PROVIDER_CHOICE_KEY, null);
  writeRemembered(null);
}

export function restoreProviderChoice(snapshot: ProviderChoiceSnapshot): void {
  write(() => sessionStorage, PROVIDER_CHOICE_KEY, snapshot.tab);
  writeRemembered(snapshot.remembered);
}
